import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Star, MessageSquare, Send, Clock, CheckCircle, AlertTriangle, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import PartnerLayout from "./PartnerLayout";

interface Review {
  id: string;
  rating: number;
  text: string;
  userName: string;
  userAvatar?: string;
  createdAt: string;
  reply?: {
    id: string;
    text: string;
    createdAt: string;
  };
  sentiment?: 'positive' | 'neutral' | 'negative';
}

interface ResponseTemplate {
  id: string;
  name: string;
  category: string;
  text: string;
}

interface DashboardStats {
  rating: number;
  ratingChange: number;
  totalReviews: number;
}

export default function PartnerReviews() {
  const { toast } = useToast();
  const restaurantId = localStorage.getItem('partnerRestaurantId');
  const [filter, setFilter] = useState<string>('all');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  
  const { data: reviews, isLoading } = useQuery<Review[]>({
    queryKey: ['/api/partner/reviews', restaurantId, filter],
    enabled: !!restaurantId,
  });

  const { data: stats } = useQuery<DashboardStats>({
    queryKey: ['/api/partner/dashboard/stats', restaurantId],
    enabled: !!restaurantId,
  });
  
  const { data: templates } = useQuery<ResponseTemplate[]>({
    queryKey: ['/api/partner/templates', restaurantId],
    enabled: !!restaurantId,
  });
  
  const replyMutation = useMutation({
    mutationFn: async ({ reviewId, text }: { reviewId: string; text: string }) => {
      return apiRequest('POST', `/api/partner/reviews/${reviewId}/reply`, { text });
    },
    onSuccess: () => {
      toast({ title: "Reply sent", description: "Your response has been posted." });
      setReplyingTo(null);
      setReplyText('');
      queryClient.invalidateQueries({ queryKey: ['/api/partner/reviews'] });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to send reply. Please try again.", variant: "destructive" });
    },
  });
  
  const handleTemplateSelect = (templateId: string) => {
    const template = templates?.find(t => t.id === templateId);
    if (template) {
      setReplyText(template.text);
    }
  };
  
  const getSentimentColor = (sentiment?: string) => {
    switch (sentiment) {
      case 'positive': return 'text-green-600';
      case 'negative': return 'text-red-600';
      default: return 'text-yellow-600';
    }
  };
  
  const getSentimentIcon = (sentiment?: string) => {
    switch (sentiment) {
      case 'positive': return <CheckCircle className="h-4 w-4" />;
      case 'negative': return <AlertTriangle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };
  
  const filteredReviews = reviews
    ?.filter(review => {
      if (filter === 'needs-reply') return !review.reply;
      if (filter === 'replied') return !!review.reply;
      if (filter === 'negative') return review.rating <= 5;
      if (filter === 'positive') return review.rating >= 8;
      return true;
    })
    .sort((a, b) => {
      if (filter !== 'all') return 0;
      const aUnreplied = !a.reply ? 1 : 0;
      const bUnreplied = !b.reply ? 1 : 0;
      if (aUnreplied !== bUnreplied) return bUnreplied - aUnreplied;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  const ratingChange = stats?.ratingChange ?? 0;
  
  return (
    <PartnerLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-reviews-title">Reviews</h1>
          <p className="text-muted-foreground">Respond to feedback and build your reputation</p>
        </div>

        {/* Rating trend bar */}
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold" data-testid="text-overall-rating">
                  {stats?.rating?.toFixed(1) ?? '—'}
                </div>
                <div className="text-xs text-muted-foreground">Overall rating</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-semibold">{stats?.totalReviews ?? '—'}</div>
                <div className="text-xs text-muted-foreground">Total reviews</div>
              </div>
              <div className="flex items-center gap-1.5 ml-auto">
                {ratingChange > 0 ? (
                  <TrendingUp className="h-5 w-5 text-green-500" />
                ) : ratingChange < 0 ? (
                  <TrendingDown className="h-5 w-5 text-red-500" />
                ) : (
                  <Minus className="h-5 w-5 text-muted-foreground" />
                )}
                <span className={`text-sm font-medium ${ratingChange > 0 ? 'text-green-600' : ratingChange < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                  {ratingChange > 0 ? '+' : ''}{ratingChange.toFixed(1)} last 30 days
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-muted-foreground">
            {filter === 'all' && filteredReviews
              ? `Showing all ${filteredReviews.length} reviews — unresponded first`
              : null}
          </p>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]" data-testid="select-review-filter">
              <SelectValue placeholder="Filter reviews" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reviews</SelectItem>
              <SelectItem value="needs-reply">Needs Reply</SelectItem>
              <SelectItem value="replied">Replied</SelectItem>
              <SelectItem value="positive">Positive (8+)</SelectItem>
              <SelectItem value="negative">Negative (5-)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        ) : filteredReviews && filteredReviews.length > 0 ? (
          <div className="space-y-4">
            {filteredReviews.map((review) => (
              <Card key={review.id} data-testid={`card-review-${review.id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <div className="flex items-center gap-1">
                          {[...Array(10)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`h-4 w-4 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-muted'}`}
                            />
                          ))}
                        </div>
                        <span className="font-medium">{review.rating}/10</span>
                        {review.sentiment && (
                          <span className={`flex items-center gap-1 text-xs ${getSentimentColor(review.sentiment)}`}>
                            {getSentimentIcon(review.sentiment)}
                            {review.sentiment}
                          </span>
                        )}
                        {!review.reply && (
                          <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
                            Needs reply
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-sm mb-3">{review.text || 'No comment provided'}</p>
                      
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{review.userName}</span>
                        <span>•</span>
                        <span>{new Date(review.createdAt).toLocaleDateString()}</span>
                      </div>
                      
                      {review.reply && (
                        <div className="mt-4 p-3 bg-primary/5 rounded-lg border-l-2 border-primary">
                          <p className="text-sm font-medium mb-1">Your Response:</p>
                          <p className="text-sm text-muted-foreground">{review.reply.text}</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            Replied on {new Date(review.reply.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-2 shrink-0">
                      {review.reply ? (
                        <Badge variant="outline" className="gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Replied
                        </Badge>
                      ) : (
                        <Dialog open={replyingTo === review.id} onOpenChange={(open) => !open && setReplyingTo(null)}>
                          <DialogTrigger asChild>
                            <Button 
                              size="sm" 
                              onClick={() => setReplyingTo(review.id)}
                              data-testid={`button-reply-${review.id}`}
                            >
                              <MessageSquare className="h-4 w-4 mr-1" />
                              Reply
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Reply to Review</DialogTitle>
                              <DialogDescription>
                                Write a thoughtful response to this customer's feedback.
                              </DialogDescription>
                            </DialogHeader>
                            
                            <div className="space-y-4 pt-4">
                              <div className="p-3 bg-muted rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                  <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                                  <span className="font-medium">{review.rating}/10</span>
                                  <span className="text-sm text-muted-foreground">by {review.userName}</span>
                                </div>
                                <p className="text-sm">{review.text || 'No comment'}</p>
                              </div>
                              
                              {templates && templates.length > 0 && (
                                <Select onValueChange={handleTemplateSelect}>
                                  <SelectTrigger data-testid="select-template">
                                    <SelectValue placeholder="Use a template..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {templates.map(template => (
                                      <SelectItem key={template.id} value={template.id}>
                                        {template.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              
                              <Textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Write your response..."
                                rows={4}
                                data-testid="textarea-reply"
                              />
                              
                              <div className="flex justify-end gap-2">
                                <Button variant="outline" onClick={() => setReplyingTo(null)}>
                                  Cancel
                                </Button>
                                <Button 
                                  onClick={() => replyMutation.mutate({ reviewId: review.id, text: replyText })}
                                  disabled={!replyText.trim() || replyMutation.isPending}
                                  data-testid="button-send-reply"
                                >
                                  <Send className="h-4 w-4 mr-1" />
                                  {replyMutation.isPending ? 'Sending...' : 'Send Reply'}
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No reviews found</p>
              <p className="text-sm text-muted-foreground">
                {filter === 'all' 
                  ? "You don't have any reviews yet. Start promoting to get your first review!"
                  : "No reviews match your current filter."}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </PartnerLayout>
  );
}
